import { useCallback, useRef, useSyncExternalStore } from 'react';
import { CRITERIO_IMPRESSAO, assinarColeta, coletaAtiva, registrar } from './coleta';

/**
 * Impressão = card >= 50% visível por 500 ms contínuos, com a aba visível. Uma vez por
 * card montado (e a coleta ainda deduplica por sessão/oferta/superfície em 30 min).
 *
 * Um único IntersectionObserver para a página inteira: o custo não cresce com o número
 * de cards. Card escondido por CSS (`hidden sm:flex`, `lg:hidden`) tem retângulo zero e
 * nunca cruza o limiar, então as duas versões do mesmo card não contam em dobro.
 *
 * Sem IntersectionObserver (navegador muito antigo) não há impressão: renderizar não é
 * ver, e o motor prefere não saber a contar errado.
 */
let observador = null;
const alvos = new Map();

function limiar() {
    return CRITERIO_IMPRESSAO.fracaoVisivel;
}

function aoCruzar(entradas) {
    entradas.forEach((entrada) => {
        const alvo = alvos.get(entrada.target);
        if (!alvo || alvo.disparado) return;
        const visivel = entrada.isIntersecting && entrada.intersectionRatio >= limiar();
        if (visivel && !alvo.timer) {
            alvo.timer = setTimeout(() => {
                alvo.timer = null;
                if (alvo.disparado || (typeof document !== 'undefined' && document.visibilityState === 'hidden')) return;
                alvo.disparado = true;
                alvo.aoVer();
                observador?.unobserve(entrada.target);
                alvos.delete(entrada.target);
            }, CRITERIO_IMPRESSAO.minVisivelMs);
        } else if (!visivel && alvo.timer) {
            clearTimeout(alvo.timer);
            alvo.timer = null;
        }
    });
}

function obterObservador() {
    if (observador) return observador;
    if (typeof IntersectionObserver === 'undefined') return null;
    observador = new IntersectionObserver(aoCruzar, { threshold: [0, limiar(), 1] });
    return observador;
}

function soltar(el) {
    const alvo = alvos.get(el);
    if (alvo?.timer) clearTimeout(alvo.timer);
    alvos.delete(el);
    observador?.unobserve(el);
}

/**
 * Observa um elemento com o mesmo critério de impressão (>= 50% por 500 ms, aba visível)
 * e chama `aoVer` uma única vez. Devolve a limpeza. Usado também pela publicidade própria,
 * que tem medição separada da coleta: esta função não registra nada sozinha.
 */
export function observarExposicao(el, aoVer) {
    if (!el || typeof aoVer !== 'function') return () => {};
    const obs = obterObservador();
    if (!obs) return () => {};
    alvos.set(el, { aoVer, timer: null, disparado: false });
    obs.observe(el);
    return () => soltar(el);
}

/** Só para testes. */
export function _reiniciarImpressao() {
    alvos.forEach((a) => a.timer && clearTimeout(a.timer));
    alvos.clear();
    observador?.disconnect();
    observador = null;
}

/**
 * `exposicao`: { surface, position, section, searchId }. Sem ela, ou com a coleta
 * desligada, devolve um ref que não faz nada.
 */
export default function useImpressao(deal, exposicao) {
    const dadosRef = useRef(null);
    dadosRef.current = () => ({
        deal_id: deal?.deal_id,
        surface: exposicao?.surface,
        position: exposicao?.position,
        section: exposicao?.section || '',
        marketplace: deal?.store_name || '',
        category: deal?.category_id || '',
    });
    const coletaLigada = useSyncExternalStore(assinarColeta, coletaAtiva, () => false);
    const ativo = Boolean(exposicao?.surface && deal?.deal_id) && coletaLigada;

    // Ref de callback com limpeza (React 19): o mesmo ref pode ir em mais de um elemento,
    // como as versões celular e desktop do card, e cada um é observado e solto sozinho.
    return useCallback((el) => {
        if (!el || !ativo) return undefined;
        return observarExposicao(el, () => registrar('impression', {
            ...dadosRef.current(), visible_ms: CRITERIO_IMPRESSAO.minVisivelMs,
        }));
    }, [ativo, exposicao?.surface, exposicao?.position, deal?.deal_id]); // eslint-disable-line react-hooks/exhaustive-deps
}
